import type { ZodiosQueryParamsByPath } from "@zodios/core";
import { makeApi } from "@zodios/core";
import { z } from "zod";

import { portableDesignerNodeInContextSchema } from "@skilitics/nautilus-core-designer-data";

import { userIdsSpecQueryParam, validationErrorSchema } from "../shared.ts";

const scopeTypeSchema = z.enum(["system", "workspace", "user", "module"]);

export type NodeTemplateScopeType = z.infer<typeof scopeTypeSchema>;

export const systemScopeSchema = z.object({
  type: z.literal("system" satisfies NodeTemplateScopeType),
});

export type SystemNodeTemplateScope = z.infer<typeof systemScopeSchema>;

export const moduleScopeSchema = z.object({
  type: z.literal("module" satisfies NodeTemplateScopeType),
  moduleId: z.string(),
});

export type ModuleNodeTemplateScope = z.infer<typeof moduleScopeSchema>;

export const workspaceScopeSchema = z.object({
  type: z.literal("workspace"),
});

export type WorkspaceNodeTemplateScope = z.infer<typeof workspaceScopeSchema>;

export const userScopeSchema = z.object({
  type: z.literal("user" satisfies NodeTemplateScopeType),
});

export type UserNodeTemplateScope = z.infer<typeof userScopeSchema>;

export const nodeTemplateScopeSchema = z.discriminatedUnion("type", [
  systemScopeSchema,
  moduleScopeSchema,
  workspaceScopeSchema,
  userScopeSchema,
]);

export type NodeTemplateScope = z.infer<typeof nodeTemplateScopeSchema>;

export const nodeTemplateBodySchema = z.object({
  title: z.string(),
  description: z.string().nullish(),
  typeId: z.string(),
  roles: z.array(z.string()),
  tags: z.array(z.string()),
  content: portableDesignerNodeInContextSchema,
  scope: nodeTemplateScopeSchema,
  category: z.string(),
  thumbnailAssetId: z.string().uuid().optional(),
});

export type NodeTemplateQueryParams = ZodiosQueryParamsByPath<typeof nodeTemplatesApi, "get", "/node-templates">;

export type NodeTemplateBody = z.infer<typeof nodeTemplateBodySchema>;

export const nodeTemplateSchema = nodeTemplateBodySchema.extend({
  id: z.number().int(),
  workspace: z.string().optional(),
  createdOn: z.coerce.date(),
  createdBy: z.string(),
  updatedOn: z.coerce.date(),
  updatedBy: z.string(),
  appVersion: z.string(),
});

export type NodeTemplate = z.infer<typeof nodeTemplateSchema>;

export const nodeTemplatePatchSchema = nodeTemplateBodySchema
  .pick({
    title: true,
    description: true,
    tags: true,
    scope: true,
    category: true,
    thumbnailAssetId: true,
  })
  .partial();

export type NodeTemplatePatch = z.infer<typeof nodeTemplatePatchSchema>;

// might want to make more idiomatic and accessible
// e.g. include[]=role:activity&include[]=typeId:question
// where it would currently be %5B%7B%22role%22%3A%22activity%22%7D%5D
const nodeFilterSchema = z.union([z.object({ typeId: z.string() }), z.object({ role: z.string() })]);

export type NodeFilter = z.infer<typeof nodeFilterSchema>;

export const nodeTemplatesApi = makeApi([
  {
    method: "get",
    path: "/node-templates/:id",
    parameters: [
      {
        type: "Path",
        name: "id",
        schema: z.number().int(),
      },
    ],
    alias: "getNodeTemplateById",
    description: "Get Node Template By Id",
    response: nodeTemplateSchema,
    errors: [
      {
        status: 404,
        schema: z.void(),
      },
    ],
  },
  {
    method: "get",
    path: "/node-templates",
    parameters: [
      {
        type: "Query",
        name: "typeId",
        schema: z.string().optional(),
      },
      {
        type: "Query",
        name: "role",
        schema: z.string().optional(),
      },
      {
        type: "Query",
        name: "createdBy",
        schema: userIdsSpecQueryParam.optional(),
      },
      {
        type: "Query",
        name: "scope",
        schema: scopeTypeSchema.optional(),
      },
      {
        type: "Query",
        name: "search",
        schema: z.string().optional(),
      },
      {
        type: "Query",
        name: "category",
        schema: z.string().optional(),
      },
      {
        type: "Query",
        name: "tags",
        schema: z.array(z.string()).optional(),
      },
      {
        type: "Query",
        name: "include",
        schema: z.array(nodeFilterSchema).optional(),
      },
      {
        type: "Query",
        name: "exclude",
        schema: z.array(nodeFilterSchema).optional(),
      },

      {
        type: "Query",
        name: "scopes",
        schema: z.array(scopeTypeSchema).optional(),
      },
      {
        type: "Query",
        name: "categories",
        schema: z.array(z.string()).optional(),
      },
      {
        type: "Query",
        name: "moduleId",
        schema: z.string().uuid().optional(),
      },
    ],
    alias: "queryNodeTemplates",
    description: "Query Node Templates",
    response: z.array(nodeTemplateSchema),
  },
  {
    method: "post",
    path: "/node-templates",
    parameters: [
      {
        type: "Body",
        name: "template",
        schema: nodeTemplateBodySchema,
      },
    ],
    alias: "createNodeTemplate",
    description: "POST node template",
    response: nodeTemplateSchema,
    status: 201,
    errors: [
      {
        status: 400,
        schema: validationErrorSchema,
      },
      {
        status: 403,
        schema: z.string(),
      },
    ],
  },
  {
    method: "patch",
    path: "/node-templates/:id",
    parameters: [
      {
        type: "Path",
        name: "id",
        schema: z.number().int(),
      },
      {
        type: "Body",
        name: "template",
        schema: nodeTemplatePatchSchema.partial(),
      },
    ],
    alias: "updateNodeTemplate",
    description: "PATCH node template",
    response: z.void(),
    status: 204,
    errors: [
      {
        status: 404,
        schema: z.string(),
      },
      {
        status: 400,
        schema: validationErrorSchema,
      },
      {
        status: 403,
        schema: z.string(),
      },
    ],
  },
  {
    method: "delete",
    path: "/node-templates/:id",
    parameters: [
      {
        type: "Path",
        name: "id",
        schema: z.number().int(),
      },
    ],
    alias: "deleteNodeTemplate",
    description: "Delete Node Template By Id",
    response: z.void(),
    errors: [
      {
        status: 403,
        schema: z.string(),
      },
    ],
  },
]);
